import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { SearchBar } from "./components/search-bar";
import { UniformFilterModal } from "./components/uniform-filter-modal";
import {
    FilterCategories,
    FilterOptions,
    commitAllStagedActiveValues,
    getActiveFiltersByFilterType
} from "./utils";
import { UniformFilterObject } from "./utils/types";

interface UniformFiltersProps {
    filterObject: UniformFilterObject;
    searchStringValue: string;
    searchInputFieldFocused: boolean;
    setSearchInputFieldFocused: Dispatch<SetStateAction<boolean>>;
    setFilterObjectValue: Dispatch<SetStateAction<UniformFilterObject>>;
    setSearchString: (searchString: string) => void;
    clearSearchField: () => void;
    runSearchWithFilters: (
        filterObject: UniformFilterObject,
        searchString: string
    ) => void;
}

type WhichFilterModalIsOpen = {
    [key in FilterCategories]: boolean;
};

const UniformSearchWithFilters = ({
    filterObject,
    searchStringValue,
    searchInputFieldFocused,
    setFilterObjectValue,
    setSearchInputFieldFocused,
    setSearchString,
    clearSearchField,
    runSearchWithFilters
}: UniformFiltersProps): JSX.Element => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const localizedMap: { [key: string]: string } = {
        app: intl.formatMessage({
            id: "uniformSearchWithFilters.filter.app",
            defaultMessage: "App"
        }),
        category: intl.formatMessage({
            id: "uniformSearchWithFilters.filter.category",
            defaultMessage: "Category"
        }),
        status: intl.formatMessage({
            id: "uniformSearchWithFilters.filter.status",
            defaultMessage: "Status"
        })
    };

    const clearFiltersText = intl.formatMessage({
        id: "uniformSearchWithFilters.clearFilters",
        defaultMessage: "Clear Filters"
    });

    const clearAllFilters = () => {
        setFilterObjectValue((prevState: UniformFilterObject) => {
            const cleared: UniformFilterObject = {} as UniformFilterObject;

            Object.keys(prevState).forEach((category) => {
                const categoryKey = category as FilterCategories;
                cleared[categoryKey] = {};

                Object.keys(prevState[categoryKey]).forEach((optionName) => {
                    cleared[categoryKey][optionName] = {
                        committedActive: false,
                        stagedActive: false
                    };
                });
            });

            runSearchWithFilters(cleared, searchStringValue);

            return cleared;
        });
    };

    const whichFilterModalIsOpenObjectDefaultState: WhichFilterModalIsOpen = {
        app: false,
        category: false,
        status: false
    };

    Object.freeze(whichFilterModalIsOpenObjectDefaultState);

    const [whichFilterModalIsOpenObject, updateWhichFilterModalIsOpenObject] =
        useState<WhichFilterModalIsOpen>(
            whichFilterModalIsOpenObjectDefaultState
        );

    // Updates the state of which filter modal is open
    // If a modal is closing, we commit all the stagedActive values
    const updateWhichFilterModalIsShown = (subFilter: FilterCategories) => {
        const isModalClosing = Object.keys(whichFilterModalIsOpenObject).filter(
            (key) => whichFilterModalIsOpenObject[key as FilterCategories]
        );

        updateWhichFilterModalIsOpenObject(() => ({
            ...whichFilterModalIsOpenObjectDefaultState,
            [subFilter]: !whichFilterModalIsOpenObject[subFilter]
        }));

        if (isModalClosing.length > 0) {
            setFilterObjectValue((prevState: UniformFilterObject) => {
                filterObject = {
                    ...prevState,
                    ...commitAllStagedActiveValues(prevState)
                };
                runSearchWithFilters(filterObject, searchStringValue);
                return filterObject;
            });
        }
    };

    // Updates the stagedActive value of the filter
    const updateSubFilterStagedValue = (
        subFilterName: FilterCategories,
        filterOptionName: string
    ) => {
        setFilterObjectValue((prevState: UniformFilterObject) => {
            return {
                ...prevState,
                [subFilterName]: {
                    ...prevState[subFilterName],
                    [filterOptionName]: {
                        ...prevState[subFilterName][filterOptionName],
                        stagedActive: !(
                            prevState[subFilterName][
                                filterOptionName
                            ] as FilterOptions
                        ).stagedActive
                    }
                }
            };
        });
    };

    // Handles logic for determining which filter modal to show
    const getFilterModal = (
        showFilter: boolean,
        filterKey: FilterCategories
    ) => {
        if (!showFilter || !whichFilterModalIsOpenObject[filterKey]) {
            return null;
        }

        return (
            <UniformFilterModal
                subFilter={filterKey}
                subFilterObject={filterObject[filterKey]}
                updateFilterObject={(filterOptionName: string) =>
                    updateSubFilterStagedValue(filterKey, filterOptionName)
                }
            />
        );
    };

    const filterButtonClickWrapper = (
        ev: React.MouseEvent<HTMLButtonElement>,
        filterCategory: FilterCategories
    ) => {
        ev.preventDefault();
        createAnalyticsEvent({
            origin: "web",
            platform: "web",
            product: "marketing",
            source: "none",
            userIdType: "atlassianAccount",
            eventComponent: "uniformFilterButton",
            event: "clicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "component",
            attributes: {
                labelEnglish: filterCategory,
                url: location.href,
                page_path: location.pathname,
                eventComponent: "uniformFilterButton"
            }
        }).fire();
        updateWhichFilterModalIsShown(filterCategory);
    };

    // If any filter modal is open, show the modal
    const showModal =
        Object.values(whichFilterModalIsOpenObject).filter(
            (isActiveValue) => isActiveValue
        ).length > 0;

    const handleDocumentClick = useCallback(
        (event: globalThis.MouseEvent) => {
            if (showModal) {
                let parentNode = event.target as HTMLElement;

                while (parentNode && parentNode.nodeName !== "BODY") {
                    if (
                        parentNode.id ===
                        "uniform-filter-modal-component-parent"
                    ) {
                        return;
                    }
                    parentNode = parentNode.parentNode as HTMLElement;
                }
            }

            // If we are here, it means the click was outside the modal
            const isModalClosing = Object.keys(
                whichFilterModalIsOpenObject
            ).filter(
                (key) => whichFilterModalIsOpenObject[key as FilterCategories]
            );

            if (isModalClosing.length > 0) {
                updateWhichFilterModalIsOpenObject(() => ({
                    ...whichFilterModalIsOpenObjectDefaultState
                }));

                setFilterObjectValue((prevState: UniformFilterObject) => {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    filterObject = {
                        ...prevState,
                        ...commitAllStagedActiveValues(prevState)
                    };
                    runSearchWithFilters(filterObject, searchStringValue);
                    return filterObject;
                });
            }
        },

        [showModal]
    );

    // Attach and detach the event listener for the document click
    // This is used to determine if there is a click outside of the filter modals
    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("click", handleDocumentClick, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("click", handleDocumentClick, false);
        };
    }, [handleDocumentClick]);

    return (
        <UFOSegment name="uniform-filters">
            <TypographyStyles>
                <section css={uniformFiltersSectionContainerStyle}>
                    <div css={uniformFiltersContainerStyle}>
                        <div css={searchButtonContainerStyle}>
                            <SearchBar
                                searchStringValue={searchStringValue}
                                setSearchString={setSearchString}
                                runSearch={() =>
                                    runSearchWithFilters(
                                        filterObject,
                                        searchStringValue
                                    )
                                }
                                searchInputFieldFocused={
                                    searchInputFieldFocused
                                }
                                setSearchInputFieldFocused={
                                    setSearchInputFieldFocused
                                }
                                clearSearchField={clearSearchField}
                            />
                        </div>
                        <div css={filterButtonContainerStyle}>
                            {/* Order matches column headers: Category, Status, Apps */}
                            {(["category", "status", "app"] as const).map(
                                (key, index) => {
                                    const filterKey = key;
                                    // Inefficient, we should look into memoizing this
                                    const numFiltersActive: number =
                                        getActiveFiltersByFilterType(
                                            filterKey,
                                            filterObject
                                        );

                                    const buttonClassNameBuilder = `body-md ${
                                        numFiltersActive ? "filter-active" : ""
                                    } ${
                                        whichFilterModalIsOpenObject[filterKey]
                                            ? "selected"
                                            : ""
                                    }`;

                                    const filterModal: JSX.Element | null =
                                        getFilterModal(showModal, filterKey);

                                    return (
                                        <div key={index}>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                className={
                                                    buttonClassNameBuilder
                                                }
                                                id="uniform-filter-modal-component-parent"
                                                css={filterButtonStyles}
                                                onClick={(
                                                    ev: React.MouseEvent<
                                                        HTMLButtonElement,
                                                        globalThis.MouseEvent
                                                    >
                                                ) =>
                                                    filterButtonClickWrapper(
                                                        ev,
                                                        filterKey
                                                    )
                                                }
                                            >
                                                {/*@ts-ignore */}
                                                {localizedMap[filterKey]}{" "}
                                                {filterDropDownArrowSVG}
                                                <span
                                                    className="fine-print"
                                                    css={
                                                        filterButtonCountStyles
                                                    }
                                                >
                                                    {numFiltersActive}
                                                </span>
                                            </Button>
                                            {filterModal}
                                        </div>
                                    );
                                }
                            )}
                            <div className="uniform-filter-button">
                                <Button
                                    variant="primary_alt"
                                    size="small"
                                    onClick={clearAllFilters}
                                    className="body-md"
                                    css={clearFiltersButtonStyle}
                                >
                                    {clearFiltersText}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};

const filterDropDownArrowSVG = (
    <svg
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="uniform-filter-dropdown-arrow"
    >
        <path
            d="M9 1.18164L5 4.818L1 1.18164"
            stroke={wacToken("color.primary.black")}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const uniformFiltersSectionContainerStyle = css({
    margin: `0 0 ${token("space.200")}`
});

const uniformFiltersContainerStyle = css({
    display: "flex",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    gap: token("space.200"),
    [media.below.md]: {
        flexWrap: "wrap"
    }
});

const filterButtonContainerStyle = css({
    position: "relative",
    display: "flex",
    flexWrap: "nowrap",
    flexBasis: "fit-content",
    transition: "opacity 200ms linear",
    gap: token("space.100"),

    [media.below.md]: {
        flexBasis: "100%",
        width: "100%",
        flexWrap: "wrap"
    }
});

const filterButtonStyles = css({
    [media.below.md]: {
        minWidth: "auto",
        flex: "1 1 auto"
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".uniform-filter-dropdown-arrow": {
        transition: "ease-in-out 0.3s transform",
        marginInlineStart: token("space.100")
    },
    // Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&.selected .uniform-filter-dropdown-arrow": {
        transform: "rotate(-180deg)"
    },
    ":hover, &.selected": {
        background: wacToken("color.neutral.light200")
    },
    "&.filter-active": {
        "background": wacToken("color.extended.purple100"),
        ":hover, &.selected": {
            background: wacToken("color.extended.purple100")
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "span": { display: "flex" },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".uniform-filter-dropdown-arrow": {
            display: "none"
        }
    }
});

const filterButtonCountStyles = css({
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    background: wacToken("color.extended.purple300"),
    borderRadius: "50%",
    marginInlineStart: token("space.100"),
    color: wacToken("color.primary.black"),
    width: token("space.250"),
    height: token("space.250"),
    fontWeight: 600,
    lineHeight: 1
});

const clearFiltersButtonStyle = css({
    whiteSpace: "nowrap"
});

const searchButtonContainerStyle = css({
    display: "flex",
    flexWrap: "nowrap",
    flexBasis: "45%",
    justifyContent: "flex-end",
    [media.below.md]: {
        maxWidth: "100%",
        flexBasis: "100%",
        justifyContent: "flex-start"
    }
});

export default UniformSearchWithFilters;
